import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Fuel, Users, Settings2, Tag, ChevronLeft, ChevronRight } from "lucide-react";

export const CarCard = ({ car, effectivePrice }) => {
  const { t } = useLanguage();
  const [imgIndex, setImgIndex] = useState(0);
  const [hovered, setHovered] = useState(false);

  const allImages = [car.image_url, ...(car.images || [])]
    .filter(Boolean)
    .filter((url, i, arr) => arr.indexOf(url) === i);

  const hasMultiple = allImages.length > 1;

  const prevImg = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImgIndex((i) => (i - 1 + allImages.length) % allImages.length);
  };

  const nextImg = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImgIndex((i) => (i + 1) % allImages.length);
  };

  const fuelLabel = t(`fleet.${car.fuel_type}`) || car.fuel_type;
  const transLabel = t(`fleet.${car.transmission}`) || car.transmission;
  const isSale = car.listing_type === "sale";
  const isBoth = car.listing_type === "both";

  const displayPrice = effectivePrice ?? car.price_per_day;

  return (
    <div
      data-testid={`car-card-${car.id}`}
      className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 transition-all duration-300 hover:shadow-xl hover:border-orange-500/20 hover:-translate-y-1"
    >
      {/* ── Image area with slider ── */}
      <div
        className="relative aspect-[4/3] overflow-hidden bg-slate-100"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <img
          src={allImages[imgIndex] || car.image_url}
          alt={`${car.brand} ${car.model}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=600";
          }}
        />

        {hasMultiple && hovered && (
          <>
            <button
              onClick={prevImg}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-all backdrop-blur-sm z-10"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={nextImg}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-all backdrop-blur-sm z-10"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}

        {hasMultiple && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {allImages.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setImgIndex(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  i === imgIndex ? "bg-white w-3" : "bg-white/50"
                }`}
              />
            ))}
          </div>
        )}

        <div className="absolute top-3 left-3 flex gap-1.5 z-10">
          <span className="inline-block bg-slate-900 text-white text-xs font-outfit font-medium px-3 py-1 rounded-full capitalize">
            {t(`fleet.${car.category}`) || car.category}
          </span>
          {(isSale || isBoth) && (
            <span data-testid={`listing-badge-${car.id}`} className="inline-flex items-center gap-1 bg-emerald-600 text-white text-xs font-outfit font-medium px-3 py-1 rounded-full">
              <Tag className="w-3 h-3" /> {isBoth ? "Sale & Rent" : "For Sale"}
            </span>
          )}
        </div>

        <div className="absolute bottom-3 right-3 z-10">
          {isSale && car.sale_price > 0 ? (
            <span className="inline-flex items-center bg-white/90 backdrop-blur-sm text-slate-900 font-outfit font-bold text-lg px-3 py-1 rounded-full shadow">
              €{car.sale_price?.toLocaleString()}
            </span>
          ) : (
            <span className="inline-flex items-center bg-white/90 backdrop-blur-sm text-slate-900 font-outfit font-bold text-lg px-3 py-1 rounded-full shadow">
              €{displayPrice}<span className="text-xs font-normal text-slate-500 ml-1">{t("fleet.perDay")}</span>
            </span>
          )}
        </div>
      </div>

      {/* ── Card body ── */}
      <div className="p-5">
        <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-1">
          {car.brand} {car.model}
        </h3>
        <p className="font-dm-sans text-sm text-slate-500 mb-3">{car.features}</p>

        <div className="flex items-center gap-4 text-slate-500 text-sm font-dm-sans mb-4">
          <span className="flex items-center gap-1">
            <Fuel className="w-3.5 h-3.5" /> {fuelLabel}
          </span>
          <span className="flex items-center gap-1">
            <Settings2 className="w-3.5 h-3.5" /> {transLabel}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> {car.seats}
          </span>
        </div>

        {isBoth && car.sale_price > 0 && (
          <div className="flex items-center justify-between text-xs font-dm-sans text-slate-500 mb-3 bg-slate-50 rounded-lg px-3 py-1.5">
            <span>Rent: €{displayPrice}/day</span>
            <span className="text-emerald-600 font-medium">Buy: €{car.sale_price?.toLocaleString()}</span>
          </div>
        )}

        <div className="flex gap-2">
          {!isSale && (
            <Link
              to={`/car/${car.id}?book=true`}
              data-testid={`book-car-${car.id}`}
              className="flex-1 text-center font-outfit font-medium text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-full py-2.5 transition-colors shadow-lg shadow-orange-500/20"
            >
              {t("fleet.bookThis")}
            </Link>
          )}
          {isSale && (
            <Link
              to={`/contact`}
              data-testid={`contact-sale-${car.id}`}
              className="flex-1 text-center font-outfit font-medium text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-2.5 transition-colors shadow-lg shadow-emerald-600/20"
            >
              Contact Us
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
